import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Row, Col, Spinner } from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css";
import ImageUploadBox from "../../components/ImageUploadBox";

function AddProduct() {
  const baseURL = process.env.REACT_APP_API_URL;
  const { id } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("profileToken");

  // Initial form state
  const initialState = {
    pro_name: "",
    brand_id: "",
    cat_id: "",
    pro_weight: "",
    w_unit: "",
  };

  // States
  const [formData, setFormData] = useState(initialState);
  const [productImage, setProductImage] = useState(null);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch brands for dropdown
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const res = await axios.get(`${baseURL}/brand/list`, { headers: { "x-access-token": token } });
        if (res.data.status) setBrands(res.data.result);
      } catch (err) {
        console.error("❌ Error fetching brands:", err);
      }
    };
    fetchBrands();
  }, [baseURL, token]);

  // Fetch categories for brand dropdown dynamically
  useEffect(() => {
    if (!formData.brand_id) {
      setCategories([]);
      setFormData((prev) => ({ ...prev, cat_id: "" }));
      return;
    }

    const fetchCategories = async () => {
      try {
        // Change here! Use same API as AddBrand
        const res = await axios.get(`${baseURL}/category/brand/${formData.brand_id}`, {
          headers: { "x-access-token": token }
        });
        if (res.data.status) setCategories(res.data.result);
      } catch (err) {
        setCategories([]);
        console.error("❌ Error fetching categories:", err);
      }
    };
    fetchCategories();
  }, [formData.brand_id, baseURL, token]);


  // Fetch product by ID (edit mode)
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        const { data } = await axios.get(`${baseURL}/product/${id}`, { headers: { "x-access-token": token } });
        if (data.status && data.result) {
          const p = data.result;
          setFormData({
            pro_name: p.pro_name || "",
            brand_id: p.brand_id ? String(p.brand_id) : "",
            cat_id: p.cat_id ? String(p.cat_id) : "",
            pro_weight: p.pro_weight || "",
            w_unit: p.w_unit || "",
          });
          setProductImage(p.pro_img || null);
        }
      } catch (err) {
        console.error("❌ Error fetching product:", err);
        Swal.fire("Error", "Failed to fetch product details", "error");
      }
    })();
  }, [id, baseURL, token]);

  // Handle form text input changes
  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [id]: value,
      // Reset category if brand changes
      ...(id === "brand_id" ? { cat_id: "" } : {})
    }));
  };

  // Reset form handler
  const handleReset = () => {
    setFormData(initialState);
    setProductImage(null);
    setCategories([]);
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    setProductImage(e.target.files[0]);
  };

  // Submit handler (update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const data = new FormData();
      Object.entries(formData).forEach(([key, value]) => data.append(key, value));

      // Only append product image if it's a file object
      if (productImage && typeof productImage !== "string") {
        data.append("pro_img", productImage);
      }

      const headers = { "Content-Type": "multipart/form-data", "x-access-token": token };

      if (id) {
        await axios.put(`${baseURL}/product/update/${id}`, data, { headers });
        Swal.fire("Success", "✅ Product updated successfully!", "success");
      } else {
        if (!productImage || typeof productImage !== "object") {
          Swal.fire("Error", "Product image is required", "error");
          setLoading(false);
          return;
        }
        await axios.post(`${baseURL}/product/add`, data, { headers });
        Swal.fire("Success", "✅ Product added successfully!", "success");
      }

      navigate("/products-list");
    } catch (err) {
      console.error("❌ Error saving product:", err);
      Swal.fire("Error", "❌ Failed to save product", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container my-4">
      <h3 className="fw-bold text-center mb-4 text-success">
        {id ? "Update Product" : "Add New Product"}
      </h3>
      <form onSubmit={handleSubmit}>
        <Row className="g-4">
          {/* Left Column */}
          <Col xs={12} md={4}>
            {/* Product Image Upload */}
            <div className="border border-success rounded-4 p-3 mb-4 text-center">
              <ImageUploadBox
                id="productImageInput"
                label="Product Image"
                value={productImage}
                onChange={handleImageChange}
                baseURL={baseURL}
                required={!id}
                isEdit={!!id}
                variant="dashed"
                height={160}
                fit="cover"
              />
            </div>
            {/* Brand Selection */}
            <div className="border border-success rounded-4 p-3 mb-4">
              <h6 className="fw-semibold mb-2">
                Select Brand <span className="text-danger">*</span>
              </h6>
              <select
                id="brand_id"
                value={formData.brand_id}
                onChange={handleChange}
                className="form-select"
                required={!id}
              >
                <option value="">Select Brand</option>
                {brands.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </select>
            </div>
            {/* Category Selection */}
            <div className="border border-success rounded-4 p-3">
              <h6 className="fw-semibold mb-2">
                Select Category <span className="text-danger">*</span>
              </h6>
              <select
                id="cat_id"
                value={formData.cat_id}
                onChange={handleChange}
                className="form-select"
                required={!id}
                disabled={!formData.brand_id}
              >
                <option value="">Select Category</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.cat_name}
                  </option>
                ))}
              </select>
            </div>
          </Col>
          {/* Right Column */}
          <Col xs={12} md={8}>
            <div className="border border-success rounded-4 p-4 mb-4">
              <h6 className="fw-semibold mb-3">Product Details</h6>
              <div className="mb-3">
                <label htmlFor="pro_name" className="form-label">
                  Product Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  id="pro_name"
                  value={formData.pro_name}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter Product Name"
                  required={!id}
                />
              </div>
              <div className="mb-3">
                <label htmlFor="pro_weight" className="form-label">Product Weight</label>
                <input
                  type="text"
                  id="pro_weight"
                  value={formData.pro_weight}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="Enter Weight (e.g., 500)"
                />
              </div>
              <div className="mb-3">
                <label htmlFor="w_unit" className="form-label">Weight Unit</label>
                <select
                  id="w_unit"
                  value={formData.w_unit}
                  onChange={handleChange}
                  className="form-select"
                >
                  <option value="">Select Weight Unit</option>
                  <option value="kg">Kg</option>
                  <option value="gm">Gm</option>
                  <option value="l">L</option>
                  <option value="ml">ml</option>
                </select>
              </div>
            </div>
            {/* Buttons */}
            <div className="d-flex justify-content-center align-items-center gap-2 mt-3">
              <Button variant="outline-dark" className="rounded-pill px-4" type="button" onClick={handleReset}>Reset</Button>
              <Button variant="success" className="rounded-pill px-4" type="submit" disabled={loading}>
                {loading ? <Spinner size="sm" /> : id ? "Update" : "Save"}
              </Button>
            </div>
          </Col>
        </Row>
      </form>
    </div>
  );
}

export default AddProduct;
